import { QuickLink } from "components";
import FileUpload, {
	DRIVERS_LICENSE_ACCEPTED_FILE_TYPES,
	splitFileUploads,
} from "elements/Input/FileUpload";
import { useEffect, useRef, useState } from "react";
import client from "utils/client";
import { sendErrorToWebhook } from "utils/sendWebhook";
import { usePropertyContext } from "../../context";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import Image from "next/image";
import {
	ExemptionsIssueVerifyingDriverLicenseMesssage,
	LicenseUploadModalSuccess,
} from "./OwnerDL";
import FileUploadSubmittingAnimation from "public/file-upload-submitting-animation.gif";
import FileUploadSuccessAnimation from "public/file-upload-success-animation.gif";
import axios from "axios";
import { useAuthContext } from "context/AuthContext";
import ButtonV2 from "elements/ButtonV2";

const SKIP_VERIFICATION_TIMEOUT = 30000;

const Owner2DL = () => {
	const [error, setError] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [success, setSuccess] = useState(false);
	const [verificationError, setVerificationError] = useState(false);
	const [showTimeoutBtn, setShowTimeoutBtn] = useState(false);
	const {
		next,
		additionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
		owner2DlFilesToUpload,
		setOwner2DlFilesToUpload,
		owner2DlExemptValue,
		owner2HasUploadedDlFile,
		setOwner2DlExemptValue,
		setOwner2HasUploadedDlFile,
		setOwner2DlVerificationErrored,
	} = useExemptionsContext();
	const { details } = usePropertyContext();
	const { userData } = useAuthContext();
	const trackEvent = useEventTracker();

	const [fileUploaded, setFileUploaded] = useState(owner2HasUploadedDlFile);

	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const verifyDriversLicense = ({ propertyId }: { propertyId: number }) => {
		if (!details?.id) return;
		setOwner2DlVerificationErrored(false);
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		intervalRef.current = setInterval(async () => {
			try {
				const response = await client.verifyExemptionsDriversLicense({
					propertyId: details?.id,
				});

				if (
					(response?.data.status === "success" ||
						response?.data.status === "failure") &&
					intervalRef.current
				) {
					clearInterval(intervalRef.current);
					setSubmitting(false);
					if (
						response?.data.status === "success" &&
						response?.data.validatedLicenses.owner2?.is_valid
					) {
						setSuccess(true);
						trackEvent({
							eventName:
								constants.EVENTS.Exemptions_Flow_Drivers_License_OCR_Success,
							data: {
								"Customer ID": userData?.id,
								"User Number": "2",
							},
						});
					} else {
						setVerificationError(true);
						trackEvent({
							eventName:
								constants.EVENTS.Exemptions_Flow_Drivers_License_OCR_Failed,
							data: {
								"Customer ID": userData?.id,
								"User Number": "2",
								"Failed Reason": "DL Verification Failed",
							},
						});
					}
				} else if (response?.status !== 200 && intervalRef.current) {
					setSubmitting(false);
					setError(true);
					clearInterval(intervalRef.current);
				}
			} catch (e) {
				if (axios.isAxiosError(e) && intervalRef.current) {
					setSubmitting(false);
					setError(true);
					clearInterval(intervalRef.current);
					trackEvent({
						eventName:
							constants.EVENTS.Exemptions_Flow_Drivers_License_OCR_Failed,
						data: {
							"Customer ID": userData?.id,
							"User Number": "2",
							"Failed Reason": "Internal Error",
						},
					});
				}
			}
		}, 3000);
	};

	const handleSubmit = async () => {
		if (!details) return;
		setError(false);
		setVerificationError(false);

		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Drivers_License_2_Answer,
			data: {
				"Action Taken": owner2DlExemptValue ? "Exempt" : "Uploaded",
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
			},
		});
		if (success || !!details?.exemption?.owner2_license_ref) {
			const trackingEventData = {
				"Action Taken": owner2DlExemptValue ? "Exempt" : "Uploaded",
			};
			next({ trackingEventData });
			return;
		}

		try {
			if (owner2DlFilesToUpload.length > 0) {
				setSubmitting(true);

				const fd = new FormData();
				fd.append("license2", owner2DlFilesToUpload[0].file);
				await client.submitExemptionsDriverLicense({
					formData: fd,
					propertyId: details!.id,
				});
				setFileUploaded(true);
				await client.sendVerificationRequestExemptionsDriversLicense({
					propertyId: details?.id,
					owner1: false,
					owner2: true,
				});
				verifyDriversLicense({ propertyId: details?.id });
			}
		} catch (e) {
			console.error(
				"Exemptions - Error uploading owner driver license #2: ",
				e
			);
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Drivers_License_OCR_Failed,
				data: {
					"Customer ID": userData?.id,
					"User Number": "2",
					"Failed Reason": "Internal Error",
				},
			});
			sendErrorToWebhook(
				"** Error Uploading files for Exemptions driver license #2 ** " + e
			);
			setSubmitting(false);
			setOwner2DlVerificationErrored(true);
			setVerificationError(false);
			setError(true);
		}
	};

	useEffect(() => {
		let timeout: NodeJS.Timeout | null = null;
		if (submitting) {
			timeout = setTimeout(() => {
				if (intervalRef.current) {
					setShowTimeoutBtn(true);
				}
			}, SKIP_VERIFICATION_TIMEOUT);
		}
		return () => {
			if (timeout && !submitting) {
				clearTimeout(timeout);
				if (intervalRef?.current) {
					clearInterval(intervalRef.current);
				}
			}
		};
	}, [submitting]);

	const handleSkip = () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Drivers_License_2_Answer,
			data: {
				"Action Taken": "Skipped",
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
				"Verification Error": verificationError,
			},
		});
		const trackingEventData = {
			"Action Taken": "Skipped",
			"Verification Error": verificationError,
		};
		next({ trackingEventData });
	};

	const handleTimeoutProceedButton = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		next();
	};

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			{submitting ? (
				<div id="exemptions-file-submitting">
					<Image
						src={FileUploadSubmittingAnimation}
						alt="File upload submitting animation"
					/>
					{showTimeoutBtn ? (
						<>
							<p className="mt-2 mb-4">
								This is taking longer than we expected, feel free to proceed
								without verifying
							</p>
							<ButtonV2
								variant="primary-outline"
								size="small"
								onClick={() => handleTimeoutProceedButton()}
								className="exemptions-show-timeout-button secondary">
								Proceed
							</ButtonV2>
						</>
					) : (
						<>
							<p className="mt-2 mb-4">
								Give us a few moments to verify your driver’s license.
							</p>
							<p className="sm">Please do not close or refresh this page.</p>
						</>
					)}
				</div>
			) : success || details?.exemption?.owner2_license_ref ? (
				<LicenseUploadModalSuccess />
			) : !!details?.exemption?.owner2_license_ref ? (
				<LicenseUploadModalSuccess
					alreadyUploaded
					owner={additionalOwnerFirstNameValue}
				/>
			) : (
				<>
					<div className="property-profile-question-card exemption-survey-card">
						<div className="property-profile-question-card-title-container">
							<p className="mb-4">
								Upload a copy of <u>only the front</u> of{" "}
								<span className="no-translate">
									{additionalOwnerFirstNameValue}'s
								</span>{" "}
								driver’s license.
							</p>
						</div>
						<FileUpload
							label="Upload front of driver’s license"
							numOfFilesAccepted={1}
							accept={DRIVERS_LICENSE_ACCEPTED_FILE_TYPES.join(",")}
							filesToUpload={owner2DlFilesToUpload}
							updateFilesToUpload={fn => {
								setOwner2DlFilesToUpload(fn(owner2DlFilesToUpload));
							}}
						/>
						{/* <div className="exemptions-survey-file-checkbox mt-4">
					<Checkbox
						name="ownerDlExemptValue"
						label={`${additionalOwnerFirstNameValue} is exempt from having to provide the required documentation.`}
						labelEl="p"
						onChange={() => {
							setOwner2DlExemptValue(!owner2DlExemptValue);
							setOwner2DlFile(undefined);
						}}
						checked={owner2DlExemptValue}
					/>
				</div> */}
					</div>
					{details?.exemption?.owner2_last_name !== userData?.last_name && (
						<div className="exemptions-azure-card mb-4">
							<p className="sm white">
								We noticed that the last names of the property owners don't
								match. In order for us to maximize the potential for your
								exemption, please provide Driver's Licenses for both homeowners.
							</p>
						</div>
					)}
				</>
			)}
			{!fileUploaded && error && (
				<p className="sm text-center terracotta mt-4">
					Whoops, something went wrong. Please try again.
				</p>
			)}
			{verificationError &&
				ExemptionsIssueVerifyingDriverLicenseMesssage(
					additionalOwnerFirstNameValue,
					additionalOwnerLastNameValue
				)}
			<ButtonV2
				size="large"
				disabled={
					submitting ||
					!(
						owner2DlFilesToUpload.length > 0 ||
						owner2DlExemptValue ||
						success ||
						details?.exemption?.owner2_license_ref
					)
				}
				type="submit"
				className="exemptions-continue-button">
				{submitting
					? "Verifying..."
					: details?.exemption?.owner2_license_ref || success
						? "Continue"
						: "Verify Driver's License"}
			</ButtonV2>
			{success ||
				!!details?.exemption?.owner2_license_ref ||
				(!submitting && (
					<div className="exemption-survey-skip">
						{fileUploaded ? (
							<>
								<ButtonV2
									variant="primary-outline"
									size="small"
									onClick={() => handleSkip()}
									className="exemptions-continue-button"
									style={{ marginTop: "16px" }}>
									Proceed With My Upload
								</ButtonV2>
							</>
						) : (
							<QuickLink asButton onClick={() => handleSkip()}>
								Upload Later
							</QuickLink>
						)}
					</div>
				))}
		</form>
	);
};

export default Owner2DL;
